import { useState, type ReactNode } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ethers } from 'ethers';
import Page from '../components/Page';
import Crumbs from '../components/Crumbs';
import Pill from '../components/Pill';
import { fmt, weiToEth, short } from '../lib/format';
import { ADDRESSES, ETHERSCAN } from '../lib/chain';
import { SLOT_HASHES, SLOT_NAMES, type SlotName } from '../lib/slots';
import { useAccount } from '../hooks/useWallet';
import { useReadContracts } from '../hooks/useContracts';
import { useBalanceOf, useBalanceOfAt, useTotalSupply, useTopHolders } from '../hooks/useToken';
import { useWriteContract } from '../hooks/useWriteContract';
import {
  useProposalCount,
  useGovernanceLastSnapshotId,
  useNamedSlotAddress,
  useProposalsMirror,
  slotNameForHash,
  type ProposalRow,
  type ProposalMirrorRow,
} from '../hooks/useGovernance';
import { useExerciseQueue } from '../hooks/useMilestones';

export default function Governance() {
  const account = useAccount();
  const contracts = useReadContracts();

  const { data: countBn, isLoading: countLoading } = useProposalCount();
  const lastSnap = useGovernanceLastSnapshotId();
  const balance = useBalanceOf(account);
  const supply = useTotalSupply();
  // #5 mirror — past ProposalSubmitted events + decoded calldata so the cards
  // can render real subjects/targets instead of "#000 — event-only".
  const mirror = useProposalsMirror();

  const N = countBn !== undefined ? Number(countBn) : 0;

  // pull every proposal in parallel
  const proposalQueries = useQueries({
    queries: Array.from({ length: N }, (_, i) => ({
      queryKey: ['metha', 'governance', 'proposalsMapping', i] as const,
      queryFn: async () => {
        const row = (await contracts.governance.proposalsMapping(BigInt(i))) as unknown as ProposalRow;
        return row;
      },
      staleTime: 20_000,
    })),
  });

  const proposals = proposalQueries
    .map((q, i) => (q.data ? { id: i, row: q.data } : null))
    .filter((x): x is { id: number; row: ProposalRow } => x !== null);

  const now = Math.floor(Date.now() / 1000);
  const open = proposals.filter(({ row }) => Number(row.deadLine) > now && !row.isSuccesfull);
  const closed = proposals.filter(({ row }) => Number(row.deadLine) <= now || row.isSuccesfull);

  const balanceMeth = balance.data !== undefined ? weiToEth(balance.data) : 0;
  const supplyMeth = supply.data !== undefined ? weiToEth(supply.data) : 0;
  const votePct = supplyMeth > 0 ? (balanceMeth / supplyMeth) * 100 : 0;

  return (
    <Page ctxLabel="governance" title="Metha · Governance" docsPath="how-it-works/governance" docsLabel="Governance">
      <Crumbs crumbs={[{ to: '/', label: 'Overview' }, { label: 'Governance' }]} />

      <div className="ph">
        <div>
          <h1>Governance <span className="acc">/ condition (c) live</span></h1>
          <p className="lede">
            Snapshot-weighted voting on protocol upgrades. Slot-based registry: any
            contract can be swapped by a successful ballot. Voting power is fixed
            at proposal submission so mid-vote transfers cannot tilt outcomes.
          </p>
        </div>
        <div className="meta">
          <span>OPEN BALLOTS <b>{countLoading ? '…' : open.length}</b></span>
          <span>TOTAL · ALL TIME <b>{N}</b></span>
          <span>LAST SNAPSHOT <b>#{lastSnap.data !== undefined ? Number(lastSnap.data) : '—'}</b></span>
        </div>
      </div>

      <div className="kpi-row mb-24" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
        <Kpi l="TOTAL SUPPLY" v={<>{fmt.meth(supplyMeth)}<span className="u">METH</span></>} d="vote-weighted denominator" />
        <Kpi l="OPEN BALLOTS" v={String(open.length)} d={`${closed.length} closed`} />
        <Kpi l="YOUR BALANCE" v={
          account ? <>{fmt.meth(balanceMeth)}<span className="u">METH</span></> :
                    <span style={{ fontSize: 14, color: 'var(--dim)' }}>connect wallet</span>
        } d={account ? `${votePct.toFixed(2)}% of supply` : '—'} />
        <Kpi l="LAST SNAPSHOT" v={lastSnap.data !== undefined ? `#${Number(lastSnap.data)}` : '—'} d="taken at period close" />
        <Kpi l="LIVE FETCH" v={proposalQueries.filter(q => q.isLoading).length === 0 ? '✓' : '…'} d={`${proposals.length} / ${N} loaded`} />
      </div>

      <div className="row r-2u1 mb-24">
        <div className="panel">
          <div className="p-head"><h3>Open ballots</h3><span className="src"><code>proposalsMapping(0..{N})</code></span></div>

          {open.length === 0 && (
            <div style={{ padding: 24, color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 12 }}>
              No open ballots right now. {N === 0 ? 'No proposals have been submitted yet.' : 'All proposals have been resolved.'}
            </div>
          )}

          {open.map(({ id, row }) => (
            <ProposalCard
              key={id}
              id={id}
              row={row}
              supplyMeth={supplyMeth}
              account={account}
              balanceMeth={balanceMeth}
              mirror={mirror.data?.[id]}
              mirrorLoading={mirror.isLoading}
            />
          ))}

          {/* Phase 3 informational row */}
          <div className="prop" style={{ background: 'var(--amber-soft)', borderLeft: '3px solid var(--amber)', paddingLeft: 13 }}>
            <div className="pt">Cond. (a) activation · phase 3 · informational only</div>
            <div className="pmeta">
              <span>SCOPE <b>Settlement</b></span>
              <span>STATUS <b style={{ color: 'var(--amber)' }}>no on-chain mechanism today</b></span>
              <span>BLOCKED BY <b>phase 2 prereqs</b></span>
            </div>
            <p className="help" style={{ margin: '0 0 10px', color: 'var(--dim)' }}>
              When the time comes, "activating cond. (a)" will not be a single proposal — it
              will be a <b style={{ color: 'var(--ink)' }}>contract swap via{' '}
              <span className="mono">submitProposal()</span></b> that replaces MethaCrowdsale and/or
              AuditingPool with versions enforcing mandatory METH settlement on funded IP.
            </p>
            <div className="prop-actions">
              <span className="mono" style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: 0.5 }}>whitepaper §11 · roadmap phase 3</span>
              <span className="btn btn-locked btn-sm">Not yet a real proposal</span>
            </div>
          </div>

          {closed.slice(0, 4).map(({ id, row }) => (
            <ClosedProposalCard key={id} id={id} row={row} supplyMeth={supplyMeth} mirror={mirror.data?.[id]} />
          ))}
        </div>

        <aside>
          <SubmitProposalCard account={account} balanceMeth={balanceMeth} supplyMeth={supplyMeth} votePct={votePct} />

          <div className="panel mb-14">
            <div className="p-head"><h3>Your voting power</h3><span className="src">current snapshot</span></div>
            <div className="p-body">
              <div className="big-stat" style={{ padding: 0, borderLeft: 0 }}>
                <div className="l">YOUR METH BALANCE</div>
                <div className="v">{fmt.meth(balanceMeth)}<span className="u">METH</span></div>
                <div className="d">{votePct.toFixed(2)}% of {fmt.meth(supplyMeth)} total supply</div>
              </div>
              <p className="help mt-14">
                Snapshot voting prevents mid-vote token transfers from tilting outcomes.
                Votes are weighted by your METH balance at proposal submission, not at vote time.
              </p>
            </div>
          </div>

          <TopHoldersPanel currentAccount={account} supplyMeth={supplyMeth} />
        </aside>
      </div>

      <ExerciseOptionQueuePanel />

      <SlotRegistry />
    </Page>
  );
}

/* -------------------- proposal cards -------------------- */

function ProposalCard({
  id, row, supplyMeth, account, balanceMeth, mirror, mirrorLoading,
}: {
  id: number;
  row: ProposalRow;
  supplyMeth: number;
  account: string | null;
  balanceMeth: number;
  mirror?: ProposalMirrorRow;
  mirrorLoading?: boolean;
}) {
  const yes = weiToEth(row.voteCount);
  const threshold = weiToEth(row.threshold);
  const supplyDenom = supplyMeth > 0 ? supplyMeth : threshold;
  const pct = supplyDenom > 0 ? (yes / supplyDenom) * 100 : 0;
  const thresholdMet = yes >= threshold;
  const remaining = Math.max(0, Number(row.deadLine) - Math.floor(Date.now() / 1000));

  return (
    <div className="prop">
      <div className="pt">Proposal #{String(id).padStart(3, '0')}
        {mirror?.proposer && (
          <span className="dim mono" style={{ fontSize: 11, fontWeight: 400, marginLeft: 8 }}>
            · proposed by {short(mirror.proposer)}
          </span>
        )}
      </div>
      {/* #5 mirror — render decoded subjects/targets when available */}
      <ProposalSubjectList mirror={mirror} loading={mirrorLoading} />
      <div className="pmeta">
        <span>SNAPSHOT <b>#{Number(row.snapshotId)}</b></span>
        <span>ENDS IN <b>{fmt.ttlShort(remaining)}</b></span>
        <span>QUORUM <b>{fmt.meth(threshold)} METH</b></span>
      </div>
      <div className="prop-bar"><div className={`fill${thresholdMet ? '' : ' amber'}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
      <div className="prop-actions">
        <span>{fmt.meth(yes)} / {fmt.meth(threshold)} METH · {thresholdMet ? 'threshold met' : 'threshold pending'}</span>
        <VoteYesButton id={id} account={account} balanceMeth={balanceMeth} snapshotId={row.snapshotId} />
      </div>
    </div>
  );
}

/** Inline list of "what the proposal actually does". Each row pairs a
 *  bytes32 subject (slot hash) with the target address it'd bind to, and
 *  marks whether the entry is an "issue" or a "slot-bind". Renders the
 *  slot name when known (Token, Auditing Pool, etc.); falls back to the
 *  raw bytes32 short form for unknown hashes. */
function ProposalSubjectList({
  mirror, loading,
}: { mirror?: ProposalMirrorRow; loading?: boolean }) {
  if (!mirror) {
    return (
      <div className="mono dim" style={{ fontSize: 11, padding: '4px 0 8px' }}>
        {loading ? 'Loading proposal subject / targets…' : 'Subject / targets unreadable for this proposal (calldata decode failed).'}
      </div>
    );
  }
  if (mirror.subject.length === 0) {
    return (
      <div className="mono dim" style={{ fontSize: 11, padding: '4px 0 8px' }}>
        Empty proposal (no targets recorded in calldata).
      </div>
    );
  }
  return (
    <div className="mono" style={{ fontSize: 11, padding: '4px 0 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      {mirror.subject.map((hash, i) => {
        const target = mirror.wallet[i] ?? '0x' + '0'.repeat(40);
        const isIssue = mirror.isIssue[i] ?? false;
        const slotName = slotNameForHash(hash);
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Pill tone={isIssue ? 'amber' : 'ctx'}>
              {isIssue ? 'issue' : 'slot-bind'}
            </Pill>
            <span style={{ color: 'var(--ink)' }}>
              {slotName ?? <span className="dim">{short(hash)}</span>}
            </span>
            <span className="dim">→</span>
            <span style={{ color: 'var(--ink)' }}>{short(target)}</span>
          </div>
        );
      })}
    </div>
  );
}

function VoteYesButton({
  id, account, balanceMeth, snapshotId,
}: {
  id: number;
  account: string | null;
  balanceMeth: number;
  snapshotId: bigint;
}) {
  const { write, status } = useWriteContract();
  // Critical pre-check: the contract verifies balanceOfAt(you, snapshotId)
  // and reverts with "No voting power at snapshot" if you got METH AFTER the
  // proposal was submitted. We read that value here and disable + explain
  // so the user doesn't burn gas on a guaranteed revert.
  const powerAtSnap = useBalanceOfAt(account, snapshotId);
  const submitting = status === 'submitting' || status === 'pending';
  const snapshotPowerMeth = powerAtSnap.data !== undefined ? weiToEth(powerAtSnap.data) : null;
  const hasSnapshotPower = snapshotPowerMeth !== null && snapshotPowerMeth > 0;
  const disabled = !account || balanceMeth === 0 || submitting || !hasSnapshotPower;

  let label: string;
  if (submitting) label = 'Voting…';
  else if (!account) label = 'Connect';
  else if (balanceMeth === 0) label = 'No METH';
  else if (powerAtSnap.isLoading) label = 'Checking power…';
  else if (!hasSnapshotPower) label = '0 power at snapshot';
  else label = `Vote YES (+${fmt.meth(snapshotPowerMeth!)})`;

  const title = !hasSnapshotPower && powerAtSnap.data !== undefined
    ? `You hold ${fmt.meth(balanceMeth)} METH now, but had ${fmt.meth(snapshotPowerMeth!)} at this proposal's snapshot (block #${String(snapshotId)}). Snapshot voting prevents post-proposal token transfers from tilting outcomes — to vote on this ballot you would have needed METH BEFORE it was submitted.`
    : undefined;

  return (
    <button
      className={`btn btn-sm ${disabled ? 'btn-locked' : 'btn-prim'}`}
      disabled={disabled}
      title={title}
      onClick={() =>
        write({
          contract: 'governance',
          fn: 'voteYes',
          args: [BigInt(id)],
          options: {
            pendingTitle: `Voting YES on proposal #${id}…`,
            successTitle: `Voted YES on #${id}`,
            invalidate: [['metha', 'governance']],
          },
        })
      }
    >
      {label}
    </button>
  );
}

function SubmitProposalCard({
  account, balanceMeth, supplyMeth, votePct,
}: {
  account: string | null;
  balanceMeth: number;
  supplyMeth: number;
  votePct: number;
}) {
  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';
  const [targetAddr, setTargetAddr] = useState('');
  const [slotName, setSlotName] = useState<SlotName>('Token Management');
  const [isIssue, setIsIssue] = useState(false);

  const slotBytes32 = SLOT_HASHES[slotName];
  const validAddr = ethers.isAddress(targetAddr.trim());
  const canSubmit = !!account && balanceMeth > 0 && validAddr && !submitting;

  async function onSubmit() {
    if (!canSubmit) return;
    await write({
      contract: 'governance',
      fn: 'submitProposal',
      args: [[targetAddr.trim()], [slotBytes32], [isIssue]],
      options: {
        pendingTitle: 'Submitting proposal…',
        successTitle: `Proposal submitted (slot: ${slotName})`,
        invalidate: [['metha', 'governance']],
      },
    });
  }

  return (
    <div className="panel mb-14">
      <div className="p-head"><h3>Submit a proposal</h3><span className="src"><code>submitProposal()</code></span></div>
      <div className="p-body">
        <p className="help" style={{ marginTop: 0 }}>
          Propose a slot-swap. Requires a connected wallet with METH balance.
          The on-chain call is{' '}
          <span className="mono">submitProposal([target], [keccak256(slot)], [isIssue])</span>.
        </p>

        <span className="field-l">Slot to upgrade</span>
        <div className="input mb-14">
          <select value={slotName} onChange={(e) => setSlotName(e.target.value as SlotName)}>
            {SLOT_NAMES.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        <span className="field-l">New target address</span>
        <div className="input mb-14">
          <input
            value={targetAddr}
            onChange={(e) => setTargetAddr(e.target.value)}
            placeholder="0x…"
          />
        </div>

        <label className="mono" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <input type="checkbox" checked={isIssue} onChange={(e) => setIsIssue(e.target.checked)} />
          Mark as issue (advisory) instead of slot swap
        </label>

        <div className="estim"><span>YOUR VOTE WEIGHT</span><b>{fmt.meth(balanceMeth)} METH ({votePct.toFixed(2)}%)</b></div>
        <div className="estim"><span>SUPPLY (vote denominator)</span><b>{fmt.meth(supplyMeth)} METH</b></div>

        <button
          className={`btn btn-prim btn-block mt-14${canSubmit ? '' : ' btn-locked'}`}
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          {submitting ? 'Submitting…' :
            !account ? 'Connect wallet to submit' :
              balanceMeth === 0 ? 'No METH balance (holders only)' :
                !validAddr ? 'Enter a valid target address' :
                  'Submit proposal'}
        </button>
      </div>
    </div>
  );
}

function ClosedProposalCard({
  id, row, supplyMeth, mirror,
}: { id: number; row: ProposalRow; supplyMeth: number; mirror?: ProposalMirrorRow }) {
  const yes = weiToEth(row.voteCount);
  const threshold = weiToEth(row.threshold);
  const denom = supplyMeth > 0 ? supplyMeth : threshold;
  const pct = denom > 0 ? (yes / denom) * 100 : 0;
  return (
    <div className="prop" style={{ opacity: 0.6 }}>
      <div className="pt">
        Proposal #{String(id).padStart(3, '0')}{' '}
        <Pill tone={row.isSuccesfull ? 'green' : 'red'}>
          {row.isSuccesfull ? 'PASSED' : 'CLOSED'}
        </Pill>
        {mirror?.proposer && (
          <span className="dim mono" style={{ fontSize: 11, fontWeight: 400, marginLeft: 8 }}>
            · proposed by {short(mirror.proposer)}
          </span>
        )}
      </div>
      {mirror && mirror.subject.length > 0 && (
        <ProposalSubjectList mirror={mirror} />
      )}
      <div className="pmeta">
        <span>SNAPSHOT <b>#{Number(row.snapshotId)}</b></span>
        <span>FINAL <b>{pct.toFixed(0)}% yes</b></span>
        <span>THRESHOLD <b>{fmt.meth(threshold)} METH</b></span>
      </div>
      <div className="prop-bar">
        <div className="fill" style={{ width: `${Math.min(100, pct)}%`, background: row.isSuccesfull ? 'var(--green)' : 'var(--red)' }} />
      </div>
    </div>
  );
}

/* -------------------- §3.3 BARDA option-exercise queue -------------------- */

/** Surfaces every milestone currently Locked behind option-exercise (BARDA §3.3).
 *  The exerciseOption fn is onlyOwner — typically OpenGovernance post-deploy —
 *  so the Exercise button will revert from a regular wallet. We render it
 *  anyway so any role can SEE the queue; only the owner can act on it. */
function ExerciseOptionQueuePanel() {
  const queue = useExerciseQueue();
  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';
  const rows = queue.data ?? [];

  return (
    <div className="panel mb-24">
      <div className="p-head">
        <h3>Option-exercise queue <span className="dim" style={{ fontWeight: 400, fontSize: 11, marginLeft: 8 }}>§3.3 · BARDA</span></h3>
        <span className="src"><code>auditingPool.exerciseOption(reqId, milestone) · onlyOwner</code></span>
      </div>

      {queue.isLoading && (
        <div style={{ padding: 24, color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 12 }}>
          Scanning requests for Locked milestones…
        </div>
      )}

      {!queue.isLoading && rows.length === 0 && (
        <div style={{ padding: 24, color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 12 }}>
          Nothing to exercise. No request currently has a Locked milestone waiting on option exercise.
        </div>
      )}

      {rows.length > 0 && (
        <div className="tbl">
          <div className="th" style={{ gridTemplateColumns: '70px 1fr 80px 120px 140px' }}>
            <span>REQ</span><span>TITLE / MILESTONE</span><span>M#</span><span>AMOUNT</span><span></span>
          </div>
          {rows.map((r) => (
            <div key={`${r.requestId}-${r.milestoneIdx}`} className="tr" style={{ gridTemplateColumns: '70px 1fr 80px 120px 140px', cursor: 'default' }}>
              <span className="pid">#{String(r.requestId).padStart(3, '0')}</span>
              <span className="who" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.title}
                <small style={{ color: 'var(--dim)' }}>{r.milestoneName}</small>
              </span>
              <span><Pill tone="amber">M{r.milestoneIdx + 1}</Pill></span>
              <span>{weiToEth(r.amountWei).toFixed(4)} ETH</span>
              <span>
                <button
                  className={`btn btn-sm ${submitting ? 'btn-locked' : 'btn-prim'}`}
                  disabled={submitting}
                  onClick={() => write({
                    contract: 'auditingPool',
                    fn: 'exerciseOption',
                    args: [BigInt(r.requestId), BigInt(r.milestoneIdx)],
                    options: {
                      pendingTitle: `Exercising option on #${r.requestId} / M${r.milestoneIdx + 1}…`,
                      successTitle: `Option exercised — M${r.milestoneIdx + 1} unlocked`,
                      invalidate: [['metha', 'auditingPool']],
                    },
                  })}
                  title="onlyOwner — will revert if your wallet isn't the contract owner (OpenGovernance post-deploy)"
                >
                  Exercise option
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      <p className="help" style={{ padding: '12px 18px', margin: 0, color: 'var(--dim)', fontSize: 11, borderTop: '1px solid var(--line)' }}>
        Milestones in this queue were declared upfront as "option-period gated" by the lab on submit.
        Each one stays Locked after the previous milestone is Approved until the Program Officer
        (typically <span className="mono">OpenGovernance</span> post-deploy) calls{' '}
        <span className="mono">exerciseOption</span>. Milestone 0 is auto-exercised at <span className="mono">acceptVerdict</span>.
      </p>
    </div>
  );
}

/* -------------------- slot registry -------------------- */

function SlotRegistry() {
  return (
    <>
      <div className="ph mt-32">
        <div>
          <h1>Slot registry <span className="acc">/ EternalStorage</span></h1>
          <p className="lede">
            Core protocol contracts live in named slots. A successful ballot can swap
            any of them — that's how the protocol upgrades without a hard fork.
          </p>
        </div>
        <div className="meta"><span>STORAGE <b>{short(ADDRESSES.EternalStorage)}</b></span></div>
      </div>

      <div className="panel">
        <div className="tbl">
          <div className="th" style={{ gridTemplateColumns: '1fr 1fr 1fr 110px 110px' }}>
            <span>SLOT (keccak256)</span><span>HUMAN-READABLE</span><span>CURRENT CONTRACT</span><span>STATUS</span><span>ACTIONS</span>
          </div>
          {SLOT_NAMES.map((name) => (
            <SlotRow key={name} name={name} />
          ))}
        </div>
      </div>
    </>
  );
}

function SlotRow({ name }: { name: SlotName }) {
  const slotHash = SLOT_HASHES[name];
  const { data: liveAddr, isLoading } = useNamedSlotAddress(name);
  const expected =
    name === 'Token'            ? ADDRESSES.MethaToken     :
    name === 'Token Management' ? ADDRESSES.MethaCrowdsale :
    name === 'Auditing Pool'    ? ADDRESSES.AuditingPool   :
    /* Governance */              ADDRESSES.OpenGovernance;

  const matches = liveAddr && liveAddr.toLowerCase() === expected.toLowerCase();
  const subtitle =
    name === 'Token'            ? 'ERC-20 with snapshot' :
    name === 'Token Management' ? 'MethaCrowdsale · auction engine' :
    name === 'Auditing Pool'    ? 'milestone state machine' :
    'this contract';

  return (
    <div className="tr" style={{ gridTemplateColumns: '1fr 1fr 1fr 110px 110px' }}>
      <span className="acc" title={slotHash}>{slotHash.slice(0, 10)}…{slotHash.slice(-4)}</span>
      <span className="who">{name}<small>keccak256("{name}") · {subtitle}</small></span>
      <span className="acc">
        {isLoading ? '…' : liveAddr ? (
          <a href={ETHERSCAN(liveAddr)} target="_blank" rel="noreferrer" className="acc">{short(liveAddr)}</a>
        ) : '—'}
      </span>
      <span>
        {isLoading ? <Pill tone="gray">loading</Pill> :
          matches ? <Pill tone="green">Verified</Pill> :
            <Pill tone="amber">Drift</Pill>}
      </span>
      <span>
        <a className="btn btn-sm" href="#submit-proposal" title={`Use the Submit Proposal panel above with slot "${name}"`}>
          Propose swap
        </a>
      </span>
    </div>
  );
}

function Kpi({ l, v, d, tone }: { l: string; v: ReactNode; d: ReactNode; tone?: 'up' | 'dn' }) {
  return (
    <div className="kpi">
      <span className="l">{l}</span>
      <span className="v">{v}</span>
      <span className={`d${tone ? ' ' + tone : ''}`}>{d}</span>
    </div>
  );
}

/**
 * Top 5 METH holders. Derived client-side by scanning Transfer events and
 * querying balanceOf — see useTopHolders. For a long-running mainnet this
 * approach would need to be replaced by an off-chain indexer, but on
 * Sepolia testnet with limited activity it's accurate and cheap enough.
 */
function TopHoldersPanel({
  currentAccount, supplyMeth,
}: {
  currentAccount: string | null;
  supplyMeth: number;
}) {
  const { data: holders, isLoading, isError } = useTopHolders(5);

  return (
    <div className="panel">
      <div className="p-head">
        <h3>Top holders · vote-weight influence</h3>
        <span className="src">
          <code>derived from Transfer events</code>
        </span>
      </div>
      <div className="p-body" style={{ padding: 0 }}>
        {isLoading && (
          <div style={{ padding: 18, color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 11 }}>
            Scanning Transfer events…
          </div>
        )}
        {isError && (
          <div style={{ padding: 18, color: 'var(--amber)', fontFamily: 'var(--mono)', fontSize: 11 }}>
            Could not load top holders (RPC scan limit hit). Will retry shortly.
          </div>
        )}
        {!isLoading && !isError && holders && holders.length === 0 && (
          <div style={{ padding: 18, color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 11 }}>
            No holders detected within the scan window. Contribute + claim to mint your first METH.
          </div>
        )}
        {!isLoading && !isError && holders && holders.length > 0 && (
          <div style={{ padding: '8px 14px 14px' }}>
            {holders.map((h, i) => {
              const balMeth = weiToEth(h.balance);
              const pct = supplyMeth > 0 ? (balMeth / supplyMeth) * 100 : 0;
              const isYou = currentAccount && h.address.toLowerCase() === currentAccount.toLowerCase();
              return (
                <div key={h.address} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--mono)', fontSize: 11, padding: '6px 0', borderTop: i === 0 ? 'none' : '1px solid var(--line)' }}>
                  <span className="dim" style={{ width: 18 }}>#{i + 1}</span>
                  <a
                    href={ETHERSCAN(h.address)}
                    target="_blank"
                    rel="noreferrer"
                    className="acc"
                    style={{ width: 96 }}
                  >
                    {short(h.address)}
                  </a>
                  {isYou && <Pill tone="ctx">YOU</Pill>}
                  <div
                    style={{
                      flex: 1,
                      height: 6,
                      background: 'var(--line)',
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.max(2, Math.min(100, pct))}%`,
                        height: '100%',
                        background: 'var(--ctx)',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                  <span style={{ width: 56, textAlign: 'right', color: 'var(--ink)' }}>
                    {pct.toFixed(2)}%
                  </span>
                  <span style={{ width: 96, textAlign: 'right', color: 'var(--dim)' }}>
                    {fmt.meth(balMeth)} METH
                  </span>
                </div>
              );
            })}
            <p className="help" style={{ marginTop: 12, marginBottom: 0, fontSize: 10 }}>
              Derived client-side from the last ~50k blocks of Transfer events.
              Refresh every 5 min. A holder who hasn't moved tokens within that
              window won't appear here — Phase 2.5 will mirror events to
              Supabase for an authoritative list.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
